import { createServerFn } from "@tanstack/react-start"

import {
  AssessmentOutcome,
  scoredAssessmentOutcomes,
} from "@/constants/terminal/assessment-outcome"
import { dataByCompanyDueReason } from "@/constants/terminal/company-due-reason"

export type ResearchState =
  | "running"
  | "queued"
  | "blocked"
  | "never"
  | "due"
  | "fresh"

const hostOf = (website: string | null) => {
  if (!website) return null
  try {
    return new URL(
      /^[a-z]+:\/\//i.test(website) ? website : `https://${website}`
    ).hostname.replace(/^www\./, "")
  } catch {
    return website.replace(/^[a-z]+:\/\//i, "")
  }
}

// One row per canonical Company, held by a Portfolio or not, with where each
// pipeline step stands: Research, the latest scored Assessment and the File.
// A fixed number of queries for the whole board, whatever its size: the board
// itself, each Company's File summary, the due set that planning reads, and
// the Companies open material Identity Reviews block.
export const loadCompaniesBoard = createServerFn({ method: "GET" }).handler(
  async () => {
    const [
      { assertAdminContext },
      { db },
      { and, desc, eq, inArray, isNotNull, isNull, max, sql },
      { alias },
      { terminalCompany, terminalIdentityReview },
      { terminalAssessment },
      { terminalCompanyRun, terminalMonitoringRun },
      { isCurrentPortfolioEntry, terminalPortfolioEntry },
      { portfolio },
      { companyDueReasons },
      { latestResearchRun, uncompilableReason },
      { readFileSummaries },
      { affectedCompaniesByRoot },
    ] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/lib/admin-db"),
      import("drizzle-orm"),
      import("drizzle-orm/pg-core"),
      import("@/db/schema/terminal-company-identity"),
      import("@/db/schema/terminal-file"),
      import("@/db/schema/terminal-schema"),
      import("@/db/schema/terminal-portfolio"),
      import("@/db/schema/public-terminal"),
      import("@/terminal/monitoring/companies-needing-monitoring"),
      import("@/terminal/monitoring/saved-research"),
      import("@/terminal/file/read-file"),
      import("@/terminal/company-identity/identity-changes"),
    ])
    await assertAdminContext()

    const latestRun = db
      .selectDistinctOn([terminalCompanyRun.company_id], {
        companyId: terminalCompanyRun.company_id,
        companyRunId: terminalCompanyRun.id,
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalMonitoringRun,
        eq(terminalMonitoringRun.id, terminalCompanyRun.monitoring_run_id)
      )
      .orderBy(
        terminalCompanyRun.company_id,
        desc(terminalMonitoringRun.started_at),
        desc(terminalCompanyRun.id)
      )
      .as("latest_run")
    // Research freezes when a Company Run sets its Source Record cutoff. A
    // launch writes every planned Company Run at once; while its Monitoring
    // Run is active, an admitted one without an outcome is running and an
    // unadmitted one is queued.
    const isRunActive = and(
      isNull(terminalMonitoringRun.finished_at),
      isNull(terminalMonitoringRun.abandoned_at)
    )
    const research = db
      .select({
        companyId: terminalCompanyRun.company_id,
        frozenAt: max(terminalCompanyRun.source_record_cutoff).as("frozen_at"),
        isRunning: sql<boolean>`bool_or(${and(
          isNotNull(terminalCompanyRun.admitted_at),
          isNull(terminalCompanyRun.outcome),
          isRunActive
        )})`.as("is_running"),
        isQueued: sql<boolean>`bool_or(${and(
          isNull(terminalCompanyRun.admitted_at),
          isNull(terminalCompanyRun.outcome),
          isRunActive
        )})`.as("is_queued"),
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalMonitoringRun,
        eq(terminalMonitoringRun.id, terminalCompanyRun.monitoring_run_id)
      )
      .groupBy(terminalCompanyRun.company_id)
      .as("research")
    const researchRun = latestResearchRun()
    // The latest scored Assessment: a live one, or an assessment-only Run's.
    const scored = db
      .selectDistinctOn([terminalAssessment.company_id], {
        companyId: terminalAssessment.company_id,
        band: terminalAssessment.band,
        score: terminalAssessment.score,
        outcome: terminalAssessment.outcome,
        completedAt: terminalAssessment.completed_at,
      })
      .from(terminalAssessment)
      .where(inArray(terminalAssessment.outcome, scoredAssessmentOutcomes))
      .orderBy(
        terminalAssessment.company_id,
        desc(terminalAssessment.completed_at)
      )
      .as("scored")
    const entryCompany = alias(terminalCompany, "entry_company")
    const canonicalEntryCompanyId = sql<string>`coalesce(${entryCompany.duplicate_of_company_id}, ${entryCompany.id})`
    // The Portfolios that hold each canonical Company through Current
    // Portfolio Entries, monitored or snapshot, as `currentPortfolioCompanyIds`
    // reads them, so a duplicate's entries count toward the Company it merged
    // into. One row per Company and Portfolio, monitored if any entry there is.
    const placements = db
      .select({
        companyId: canonicalEntryCompanyId.as("canonical_company_id"),
        name: portfolio.name,
        slug: portfolio.slug,
        isMonitoring:
          sql<boolean>`bool_or(${terminalPortfolioEntry.is_monitoring})`.as(
            "is_monitoring"
          ),
      })
      .from(terminalPortfolioEntry)
      .innerJoin(
        entryCompany,
        eq(entryCompany.id, terminalPortfolioEntry.company_id)
      )
      .innerJoin(
        portfolio,
        eq(portfolio.id, terminalPortfolioEntry.portfolio_id)
      )
      .where(isCurrentPortfolioEntry)
      .groupBy(canonicalEntryCompanyId, portfolio.name, portfolio.slug)
      .as("placements")
    const portfolios = db
      .select({
        companyId: placements.companyId,
        portfolios: sql<
          { name: string; slug: string; isMonitoring: boolean }[]
        >`json_agg(json_build_object('name', ${placements.name}, 'slug', ${placements.slug}, 'isMonitoring', ${placements.isMonitoring}) order by ${placements.name})`.as(
          "portfolios"
        ),
      })
      .from(placements)
      .groupBy(placements.companyId)
      .as("portfolios")

    // Few reviews are open and material, so the blocked set is read once from
    // them rather than tested per Company, which scanned every open review for
    // each row. The queue reads it the same way.
    const readBlockedIds = async () => {
      const reviews = await db
        .select({ companyIds: terminalIdentityReview.affected_company_ids })
        .from(terminalIdentityReview)
        .where(
          and(
            isNull(terminalIdentityReview.resolved_at),
            eq(terminalIdentityReview.is_material, true)
          )
        )
      const companyIdsByRoot = await db.transaction((tx) =>
        affectedCompaniesByRoot(tx, [
          ...new Set(reviews.flatMap(({ companyIds }) => companyIds)),
        ])
      )
      return new Set([...companyIdsByRoot.values()].flat())
    }

    const [rows, due, blockedIds] = await Promise.all([
      db
        .select({
          companyId: terminalCompany.id,
          displayName: terminalCompany.display_name,
          slug: terminalCompany.slug,
          website: sql<string | null>`${terminalCompany.intake}->>'website'`,
          portfolios: sql<
            { name: string; slug: string; isMonitoring: boolean }[]
          >`coalesce(${portfolios.portfolios}, '[]'::json)`,
          latestCompanyRunId: latestRun.companyRunId,
          frozenAt: research.frozenAt,
          isRunning: research.isRunning,
          isQueued: research.isQueued,
          researchRunCompanyId: researchRun.companyId,
          researchStopReason: researchRun.stopReason,
          scoredBand: scored.band,
          scoredScore: scored.score,
          scoredOutcome: scored.outcome,
          scoredAt: scored.completedAt,
        })
        .from(terminalCompany)
        .leftJoin(portfolios, eq(portfolios.companyId, terminalCompany.id))
        .leftJoin(latestRun, eq(latestRun.companyId, terminalCompany.id))
        .leftJoin(research, eq(research.companyId, terminalCompany.id))
        .leftJoin(researchRun, eq(researchRun.companyId, terminalCompany.id))
        .leftJoin(scored, eq(scored.companyId, terminalCompany.id))
        .where(isNull(terminalCompany.duplicate_of_company_id))
        .orderBy(terminalCompany.display_name),
      companyDueReasons(),
      readBlockedIds(),
    ])
    const dueById = new Map(due.map((row) => [row.companyId, row]))
    const summaryByCompanyId = await readFileSummaries(
      rows.map(({ companyId }) => ({ companyId }))
    )
    return rows.map((row) => {
      const summary = summaryByCompanyId.get(row.companyId)
      const dueRow = dueById.get(row.companyId)
      const dueReasons = dueRow?.reasons ?? []
      const researchUncompilableReason = uncompilableReason(
        row.researchRunCompanyId ? { stopReason: row.researchStopReason } : null
      )
      // A due Company needs Research when one of its reasons does or its
      // saved Research cannot be compiled, and otherwise only a File, which a
      // compile-only Run writes.
      let dueStep: "Research" | "File" | null = null
      if (
        dueReasons.some(
          (reason) => dataByCompanyDueReason[reason].step === "Research"
        ) ||
        (dueReasons.length > 0 && researchUncompilableReason !== null)
      )
        dueStep = "Research"
      else if (dueReasons.length > 0) dueStep = "File"
      let researchState: ResearchState = "fresh"
      if (row.isRunning) researchState = "running"
      else if (row.isQueued) researchState = "queued"
      else if (blockedIds.has(row.companyId)) researchState = "blocked"
      else if (row.frozenAt === null) researchState = "never"
      else if (dueStep === "Research") researchState = "due"
      return {
        companyId: row.companyId,
        displayName: row.displayName,
        slug: row.slug,
        host: hostOf(row.website),
        portfolios: row.portfolios,
        latestCompanyRunId: row.latestCompanyRunId,
        dueReasons,
        dueItems: dueRow?.items ?? {},
        research: {
          state: researchState,
          frozenAt: row.frozenAt,
          uncompilableReason: researchUncompilableReason,
        },
        assessment:
          row.scoredOutcome && row.scoredAt
            ? {
                band: row.scoredBand,
                score: row.scoredScore,
                outcome: row.scoredOutcome,
                completedAt: row.scoredAt,
              }
            : null,
        file: {
          isLive: summary !== undefined,
          // When any of the File's parts last changed.
          updatedAt: summary?.updatedAt ?? null,
          // Research is complete and current, so a compile-only Run writes
          // the File; a Company in a Run or blocked waits on that instead.
          isDue: dueStep === "File" && researchState === "fresh",
          // An assessment-only Run rescored the Company after its live
          // Assessment and moved its band or score, so the File no longer
          // reflects the latest score. The latest scored Assessment is newer
          // than the live one whenever it is not live itself; a rescore that
          // lands where the File stands leaves nothing behind.
          isBehind:
            summary !== undefined &&
            row.scoredOutcome === AssessmentOutcome.ASSESSED &&
            (row.scoredBand !== summary.assessment.band ||
              row.scoredScore !== summary.assessment.score),
          // The live Assessment's own score, which the latest score moved
          // from.
          currentBand: summary?.assessment.band ?? null,
          currentScore: summary?.assessment.score ?? null,
        },
      }
    })
  }
)

export type CompaniesBoardRow = Awaited<
  ReturnType<typeof loadCompaniesBoard>
>[number]
